import React, { useState, useEffect, useRef } from 'react';
import { getToken } from '../../utils/tokenUtils';
import { Search, Filter, Eye, Edit, RefreshCw, Download, Calendar, Users, Loader, CircleDollarSign, CircleDollarSignIcon, DollarSign, FileText, CheckCircle, Clock, AlertCircle, XCircle, MoreVertical } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";
import { FaDiceOne, FaProcedures } from 'react-icons/fa';
import ReactPaginate from 'react-paginate';
import Notification from "../../components/UI/Notification";
import { X } from "lucide-react";
import StatusBadge from '../../components/UI/StatusBadge';

const PayrollRecords = () => {
  const [records, setRecords] = useState([]);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true); 
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [remarksModal, setRemarksModal] = useState({ open: false, recordId: null, newStatus: '', remarks: '' });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    employee_id: '',
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });
  const [currentPage, setCurrentPage] = useState(1);
  const token = getToken();

  useEffect(() => {
    fetchRecords();
  }, [filters, pagination.current_page]);

  const fetchRecords = async (page=1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        year: filters.year,
        month: filters.month,
        // per_page: pagination.per_page,
        page: page
      });

      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.status) params.append('status', filters.status);

      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls?${params}` , {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      // set pagination
      setPagination({
        currentPage: data?.data.current_page || 1,
        totalPages: data?.data.last_page || 1,
        totalItems: data?.data.total || 0,
        perPage: data?.data.per_page || 20,
      });

      let filteredData = data.data.data || [];
      if (filters.search) {
        filteredData = filteredData.filter(record => 
          (record.employee_name || '').toLowerCase().includes(filters.search.toLowerCase()) ||
          record.employee_id.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      
      setRecords(filteredData);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        last_page: data.last_page || 1
      }));
    } catch (error) {
      console.error('Error fetching payroll records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecordDetails = async (recordId) => {
    try {
      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${recordId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setSelectedRecord(data.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching record details:', error);
    }
  };

  const updateRecordStatus = async (recordId, newStatus, remarks = '') => {
    try {
      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus, remarks })
      });

      if (response.status === 404){
        setNotification({
          message: "404 not found",
          type: "error"
        });
      }

      if (response.ok) {
        const data = await response.json(); // <-- parse the JSON

        setNotification({
          message: data.message || "Payroll record updated successfully",
          type: "success"
        });

        fetchRecords();
        if (selectedRecord?.id === recordId) {
          fetchRecordDetails(recordId);
        }
      }

    } catch (error) {
      console.error('Error updating status:', error.message);
    }
  };

  const openRemarksModal = (recordId, newStatus, preset = '') => {
    setRemarksModal({ open: true, recordId, newStatus, remarks: preset || '' });
  };

  const submitRemarks = async () => {
    const { recordId, newStatus, remarks } = remarksModal;
    if (!recordId || !newStatus) {
      setRemarksModal({ open: false, recordId: null, newStatus: '', remarks: '' });
      return;
    }
    await updateRecordStatus(recordId, newStatus, remarks || '');
    setRemarksModal({ open: false, recordId: null, newStatus: '', remarks: '' });
  };

  // const regeneratePayroll = async (employeeId) => {
  //   if (window.confirm('Are you sure you want to regenerate payroll for this employee?')) {
  //     try {
  //       const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/regenerate-employee-payroll`, {
  //         method: 'POST',
  //         headers: {
  //           'Content-Type': 'application/json',
  //           'Authorization': `Bearer ${token}`,
  //         },
  //         body: JSON.stringify({
  //           employee_id: employeeId,
  //           year: filters.year,
  //           month: filters.month
  //         })
  //       });
  //       if (response.status === 404){
  //         setNotification({
  //           message: "404 not found",
  //           type: "error"
  //         });
  //       }  
  //       if (response.ok) {           
  //         setNotification({
  //           message: response.message,
  //           type: "success"
  //         });
  //         fetchRecords();
  //       }
  //     } catch (error) {
  //       console.error('Error regenerating payroll:', error);
  //       setNotification({
  //         message: error.message,
  //         type: "error"
  //       });
  //     }
  //   }
  // };

  const regeneratePayroll = async (employeeId) => {
    if (window.confirm('Are you sure you want to regenerate payroll for this employee?')) {
      try {
        const response = await fetch(
          `https://hrmspayroll.prepstripe.com/api/payroll/regenerate-employee-payroll`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              employee_id: employeeId,
              year: filters.year,
              month: filters.month,
            }),
          }
        );

        const result = await response.json();

        if (response.status === 404) {
          setNotification({
            message: "404 not found",
            type: "error",
          });
          return;
        }

        if (!response.ok) {
          setNotification({
            message: result.message || "Failed to regenerate payroll",
            type: "error",
          });
          return;
        }

        setNotification({
          message: result.message || "Payroll regenerated successfully",
          type: "success",
        });

        // ✅ Extract the updated data from API response
        const updatedInfo = result?.data?.results?.[0];
        if (updatedInfo) {
          setRecords((prevRecords) =>
            prevRecords.map((record) =>
              record.employee_id === updatedInfo.employee_id
                ? {
                    ...record,
                    status: updatedInfo.status,
                    payroll_id: updatedInfo.payroll_id,
                    net_salary: updatedInfo.net_salary,
                    salary_date: updatedInfo.salary_date,
                  }
                : record
            )
          );
        }
      } catch (error) {
        console.error('Error regenerating payroll:', error);
        setNotification({
          message: error.message,
          type: "error",
        });
      }
    }
  };


  // Actions menu component
  const ActionsColumn = React.memo(({ row }) => {
    const buttonRef = useRef(null);
    return (
      <div className="flex items-center justify-center relative">
        <button
          ref={buttonRef}
          onClick={() => setOpenMenu(openMenu === row.id ? null : row.id)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Actions"
        >
          <MoreVertical className="h-4 w-4 text-gray-600" />
        </button>
        {openMenu === row.id && (
          <DropdownMenu buttonRef={buttonRef} onClose={() => setOpenMenu(null)}>
            <button
              onClick={() => { fetchRecordDetails(row.id); setOpenMenu(null); }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors"
            >
              View Details
            </button>
            <button
              onClick={() => { regeneratePayroll(row.employee_id); setOpenMenu(null); }}
              className="w-full text-left px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 transition-colors"
            >
              Regenerate
            </button>
            {row.status !== 'paid' && (
              <>
                {row.status === 'draft' && (
                  <button
                    onClick={() => { setRemarksModal({ open: true, recordId: row.id, newStatus: 'processed', remarks: '' }); setOpenMenu(null); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Mark as Processed
                  </button>
                )}
                {row.status === 'processed' && (
                  <button
                    onClick={() => { setRemarksModal({ open: true, recordId: row.id, newStatus: 'approved', remarks: '' }); setOpenMenu(null); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Approve
                  </button>
                )}
                {row.status === 'approved' && (
                  <button
                    onClick={() => { setRemarksModal({ open: true, recordId: row.id, newStatus: 'paid', remarks: '' }); setOpenMenu(null); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Mark as Paid
                  </button>
                )}
              </>
            )}
          </DropdownMenu>
        )}
      </div>
    );
  });

  const getStatusInfo = (status) => {
    switch (status) {
      case 'paid':
        return { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100', text: 'text-green-800' };
      case 'approved':
        return { icon: CheckCircle, color: 'text-blue-500', bg: 'bg-blue-100', text: 'text-blue-800' };
      case 'processed':
        return { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-100', text: 'text-yellow-800' };
      case 'draft':
        return { icon: Edit, color: 'text-gray-500', bg: 'bg-gray-100', text: 'text-gray-800' };
      default:
        return { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100', text: 'text-red-800' };
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchRecords(newPage);
    }
  };

  return (
    <div className="p-3">

      {/* notification */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className='flex flex-col gap-2'>
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Payroll Records</h1>
            <p className="text-gray-600 mt-2">View and manage processed payroll records</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={fetchRecords}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-gray-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-md shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.year}
                onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.month}
                onChange={(e) => setFilters(prev => ({ ...prev, month: parseInt(e.target.value) }))}>
                {months.map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}>
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="processed">Processed</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.employee_id}
                onChange={(e) => setFilters(prev => ({ ...prev, employee_id: e.target.value }))}
                placeholder="EMP001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({
                  year: new Date().getFullYear(),
                  month: new Date().getMonth() + 1,
                  employee_id: '',
                  status: '',
                  search: ''
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-center">
                <Filter className="h-4 w-4 mr-2" />
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Records Table */}
        <div className="bg-white rounded-md shadow-md overflow-hidden">
          <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
            <table className="min-w-full table-auto md:table-fixed text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
                <tr>
                  <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">EMP ID</th>
                  <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">Period</th>
                  <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">Gross Salary</th>
                  <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">Deductions</th>
                  <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">Net Salary</th>
                  <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">Status</th>
                 <th className="whitespace-nowrap text-center px-6 py-4 font-semibold text-black dark:text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">Loading payroll records...</td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">No payroll records found</td>
                  </tr>
                ) : (
                  records.map((record) => {
                    const statusInfo = getStatusInfo(record.status);
                    const StatusIcon = statusInfo.icon;
                    
                    return (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td
                          onClick={() => { fetchRecordDetails(record.id); }}
                          className="px-6 py-4 whitespace-nowrap text-blue-500 hover:underline hover:underline-offset-4 cursor-pointer">
                          {record.employee_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {months[record.payroll_month - 1]} {record.payroll_year}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ₹{record.gross_salary?.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ₹{record.total_deductions?.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ₹{record.net_salary?.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap ">
                          <StatusBadge status={record?.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <ActionsColumn row={record} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* pagination compo */}
        {/* <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          disabledClassName='opacity-50 cursor-not-allowed'
          disabledLinkClassName='pointer-events-none'
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800'
          nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
          renderOnZeroPageCount={null}
          containerClassName="flex items-center justify-center mt-4 mb-1 font-poppins"
          pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          /> */}

          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName='opacity-50 cursor-not-allowed'
            disabledLinkClassName='pointer-events-none'
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
            previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-2 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />

        {/* Record Details Modal */}
        {showDetailsModal && selectedRecord && (
          <div 
            className="fixed inset-0 bg-black/60 flex justify-center items-center md:items-center z-[1001] backdrop-blur-md transition-all duration-300"
            onClick={() => setShowDetailsModal(false)}>
            <div 
              className="relative top-2 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}>
              <div className="">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">
                    Payroll Details - {selectedRecord.employee_name || selectedRecord.employee_id}
                  </h3>
                  <div className="flex items-center space-x-3">
                    <span>
                      {/* {selectedRecord.status} */}
                      <StatusBadge status={selectedRecord.status} />
                    </span>
                    <button
                        type="button"
                        onClick={() => {
                        setShowDetailsModal(false);
                        }} 
                        className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                        aria-label="Close modal">
                        <X 
                            size={20} 
                            className="text-gray-600 transition-colors duration-200" 
                        />
                        <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Employee Information */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Employee Information</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Employee ID:</span>
                        <span className="text-sm font-medium text-gray-900">{selectedRecord.employee_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Name:</span>
                        <span className="text-sm font-medium text-gray-900">{selectedRecord.employee_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Period:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {months[selectedRecord.month - 1]} {selectedRecord.year}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Generated On:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(selectedRecord.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Salary Breakdown */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Salary Breakdown</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Basic Salary:</span>
                        <span className="text-sm font-medium text-gray-900">₹{selectedRecord.basic_salary?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Total Earnings:</span>
                        <span className="text-sm font-medium text-gray-900">₹{selectedRecord.total_earnings?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Total Deductions:</span>
                        <span className="text-sm font-medium text-gray-900">₹{selectedRecord.total_deductions?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between border-t border-gray-300 pt-2">
                        <span className="text-sm font-semibold text-gray-900">Net Salary:</span>
                        <span className="text-sm font-bold gray-900">₹{selectedRecord.net_salary?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Earnings Components */}
                {selectedRecord.earnings_breakdown && (
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Earnings Breakdown</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {selectedRecord.earnings_breakdown.map((earning, index) => (
                        <div key={index} className="p-3 border border-green-200 rounded-lg bg-green-50">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-green-900">{earning.name}</span>
                            <span className="text-sm font-bold text-green-800">₹{earning.amount?.toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Deductions Components */}
                {selectedRecord.deductions_breakdown && (
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Deductions Breakdown</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {selectedRecord.deductions_breakdown.map((deduction, index) => (
                        <div key={index} className="p-3 border border-red-200 rounded-lg bg-red-50">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-red-900">{deduction.name}</span>
                            <span className="text-sm font-bold text-red-800">₹{deduction.amount?.toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status Update Actions */}
                {/* {selectedRecord.status !== 'paid' && (
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-blue-900 mb-3">Update Status</h4>
                    <div className="flex space-x-3">
                      {selectedRecord.status === 'draft' && (
                        <button
                          onClick={() => setRemarksModal({ open: true, recordId: selectedRecord.id, newStatus: 'processed', remarks: '' })}
                          className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 flex items-center"
                        >
                          <Clock className="h-4 w-4 mr-2" />
                          Mark as Processed
                        </button>
                      )}
                      {selectedRecord.status === 'processed' && (
                        <button
                          onClick={() => setRemarksModal({ open: true, recordId: selectedRecord.id, newStatus: 'approved', remarks: '' })}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </button>
                      )}
                      {selectedRecord.status === 'approved' && (
                        <button
                          onClick={() => setRemarksModal({ open: true, recordId: selectedRecord.id, newStatus: 'paid', remarks: '' })}
                          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
                        >
                          <DollarSign className="h-4 w-4 mr-2" />
                          Mark as Paid
                        </button>
                      )}
                    </div>
                  </div>
                )} */}

                <div className="flex items-center gap-2 justify-end pt-6">
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="px-12 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
                  >
                    Close
                  </button>
                  {selectedRecord.status === 'draft' && (
                    <button
                      onClick={() => setRemarksModal({ open: true, recordId: selectedRecord.id, newStatus: 'processed', remarks: '' })}
                      className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 flex items-center"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Mark as Processed
                    </button>
                  )}
                  {selectedRecord.status === 'processed' && (
                    <button
                      onClick={() => setRemarksModal({ open: true, recordId: selectedRecord.id, newStatus: 'approved', remarks: '' })}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 flex items-center"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </button>
                  )}
                  {selectedRecord.status === 'approved' && (
                    <button
                      onClick={() => setRemarksModal({ open: true, recordId: selectedRecord.id, newStatus: 'paid', remarks: '' })}
                      className="bg-green-600 text-white px-4 py-2 rounded-md font-medium hover:bg-green-700 flex items-center"
                    >
                      ₹
                      Mark as Paid
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remarks Modal */}
        {remarksModal.open && (
          <div className="fixed inset-0 bg-black/60 flex justify-center items-end md:items-center z-[1001] backdrop-blur-md transition-all duration-300">
            <div className="absolute inset-0 bg-black/50" onClick={() => setRemarksModal({ open: false, recordId: null, newStatus: '', remarks: '' })}></div>
            <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-md shadow-xl">
              
              {/* <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Remarks (optional)</h3>
                <button
                  onClick={() => setRemarksModal({ open: false, recordId: null, newStatus: '', remarks: '' })}
                  className="text-gray-400 hover:text-gray-600">
                  <XCircle className="h-5 w-5" />
                </button>
              </div> */}

              <div className="relative">
                <div className="absolute inset-0  dark:from-gray-800 dark:to-gray-850"></div>
                <div className="relative flex items-center justify-between px-4 py-3">
                  <div className="flex items-center space-x-2">
                    {/* <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div> */}
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                      Add Remarks (optional)
                    </h2>
                  </div>
                  
                  {/* Enhanced close button */}
                  <button
                    type="button"
                    onClick={() => setRemarksModal({ open: false, recordId: null, newStatus: '', remarks: '' })}
                    className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                    aria-label="Close modal"
                  >
                    <X 
                      size={20} 
                      className="text-gray-600  transition-colors duration-200" 
                    />
                    <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Remarks</label>
                  <textarea
                    rows={4}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    placeholder="Type any remarks (optional)"
                    value={remarksModal.remarks}
                    onChange={(e) => setRemarksModal(prev => ({ ...prev, remarks: e.target.value }))}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setRemarksModal({ open: false, recordId: null, newStatus: '', remarks: '' })}
                    className="px-7 py-2 text-black hover:bg-gray-200 font-medium dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md">
                    Cancel
                  </button>
                  <button
                    onClick={async () => { await updateRecordStatus(remarksModal.recordId, remarksModal.newStatus, remarksModal.remarks || ''); setRemarksModal({ open: false, recordId: null, newStatus: '', remarks: '' }); }}
                    className="px-7 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700">
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollRecords; 
